import type { PhotoItemDto } from '@customTypes/types';
import { PhotoIcon } from '@heroicons/react/24/outline';
import {
  Box,
  Flex,
  List,
  ListItem,
  RadioGroup,
  Space,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { IMAGE_MIME_TYPE } from '@mantine/dropzone';

import { PageLoading } from '@/pages/PageLoading';
import { usePaymentFrequencyList } from '@/pages/club/onboard/components/constants/paymentsFrequencyList';
import { useHousingPage } from '@/pages/club/onboard/components/stages/housing/hooks/useHousingPage';
import { translate } from '@/shared/i18n/translate';
import { LBPhotoDropZone } from '@/shared/modules';
import { useCurrencyStore } from '@/shared/store/store';
import {
  LBButton,
  LBNumberCurrencyInput,
  LBNumberInput,
  LBRadio,
  LBSelect
} from '@/shared/ui-kit/components';

export const Housing = () => {
  const {
    state: { isLoading, form, housingImages },
    functions: { onSubmit, setHousingImages }
  } = useHousingPage();

  const paymentFrequencyList = usePaymentFrequencyList();

  const profileCurency = useCurrencyStore((state) => state.profileCurrency);
  const size = import.meta.env.VITE_PHOTO_MAX_SIZE as string;

  if (isLoading) return <PageLoading />;

  return (
    <form onSubmit={onSubmit}>
      <Title order={1}>
        {translate('pages.onboarding.club.housing.title')}
      </Title>
      <Space h={32} />
      <Stack gap={24}>
        <Box>
          <Text>{translate('commons.rentPayment')}</Text>
          <Space h={8} />
          <RadioGroup
            value={JSON.stringify(form.getValues().isHousingPaidByClub)}
            onChange={(value) =>
              form.setFieldValue(
                'isHousingPaidByClub',
                JSON.parse(value) as boolean
              )
            }
          >
            <Stack gap={8}>
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.paydByClub')}
                value='true'
              />
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.paydByArtist')}
                value='false'
              />
            </Stack>
          </RadioGroup>
          <Space h={8} />
          <List withPadding listStyleType='none'>
            <ListItem>
              <Flex align='center' gap={8} justify='space-between'>
                <LBNumberCurrencyInput
                  key={form.key('housingPrice')}
                  currencySymbol={profileCurency}
                  {...form.getInputProps('housingPrice')}
                  placeholder='0'
                  disabled={
                    form.getValues().isHousingPaidByClub || form.submitting
                  }
                />
                <LBSelect
                  allowDeselect={false}
                  data={paymentFrequencyList}
                  {...form.getInputProps('housingPaymentFrequency')}
                  key={form.key('housingPaymentFrequency')}
                  disabled={
                    form.getValues().isHousingPaidByClub || form.submitting
                  }
                />
              </Flex>
            </ListItem>
          </List>
        </Box>
        <Box>
          <Text>{translate('commons.utilitiesPayment')}</Text>
          <Space h={8} />
          <RadioGroup
            value={JSON.stringify(form.getValues().isUtilityFeePaidByClub)}
            onChange={(value) =>
              form.setFieldValue(
                'isUtilityFeePaidByClub',
                JSON.parse(value) as boolean
              )
            }
          >
            <Stack gap={8}>
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.paydByClub')}
                value='true'
              />
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.paydByArtist')}
                value='false'
              />
            </Stack>
          </RadioGroup>
          <Space h={8} />
          <List withPadding listStyleType='none'>
            <ListItem>
              <Flex align='center' gap={8} justify='space-between'>
                <LBNumberCurrencyInput
                  currencySymbol={profileCurency}
                  placeholder='0'
                  disabled={
                    form.getValues().isUtilityFeePaidByClub || form.submitting
                  }
                  {...form.getInputProps('utilityFee')}
                  key={form.key('utilityFee')}
                />
                <LBSelect
                  allowDeselect={false}
                  disabled={
                    form.getValues().isUtilityFeePaidByClub || form.submitting
                  }
                  {...form.getInputProps('utilityPaymentFrequency')}
                  key={form.key('utilityPaymentFrequency')}
                  data={paymentFrequencyList}
                />
              </Flex>
            </ListItem>
          </List>
        </Box>
        <Box>
          <Text>{translate('commons.clubProvidedAccommodation')}</Text>
          <Space h={8} />
          <RadioGroup
            value={JSON.stringify(form.getValues().isClubHousingMandatory)}
            onChange={(value) =>
              form.setFieldValue(
                'isClubHousingMandatory',
                JSON.parse(value) as boolean
              )
            }
          >
            <Stack gap={8}>
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.required')}
                value='true'
              />
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.optional')}
                value='false'
              />
            </Stack>
          </RadioGroup>
        </Box>
        <Box>
          <Text>{translate('commons.clubProvidedAccommodation')}</Text>
          <Space h={4} />
          <Flex align='center' justify='space-between'>
            <Text>{translate('commons.peopleInRoom')}</Text>
            <LBNumberInput
              key={form.key('peoplePerRoom')}
              disabled={form.submitting}
              maw={124}
              placeholder='0'
              {...form.getInputProps('peoplePerRoom')}
            />
          </Flex>
        </Box>
        <Box>
          <Title order={3}>{translate('commons.housingPhoto')}</Title>
          <Space h={4} />
          <Text>{translate('commons.noteEditLater')}</Text>
          <Space h={8} />
          <LBPhotoDropZone
            accept={IMAGE_MIME_TYPE}
            disabled={form.submitting}
            maxFiles={5}
            initialValue={
              housingImages
                ? housingImages.map((item) => (item as PhotoItemDto).filePath)
                : []
            }
            onFilesChange={setHousingImages}
          >
            <Flex align='center' direction='column' py={70}>
              <PhotoIcon color='gray' width={64} />
              <Text c='blue' fz='sm'>
                {translate('commons.uploadManyFromDevice')}
              </Text>
              <Text c='blue' fz='sm' ta='center'>
                {translate('commons.photoParams', { size })}
              </Text>
            </Flex>
          </LBPhotoDropZone>
        </Box>
      </Stack>
      <Space h={32} />
      <LBButton fullWidth loading={form.submitting} type='submit'>
        {translate('commons.finish')}
      </LBButton>
    </form>
  );
};
